import React, { useState, useRef, useEffect, useCallback } from "react";
import { useContextMenuSettings } from "../../contexts/ContextMenuContext";
import "../../css/ContextMenu.css";

/**
 * Reusable ContextMenu component for Bluebird Task Management.
 * 
 * Renders a positioned dropdown with a list of actions.
 * Supports icons, separators, disabled items, and keyboard navigation.
 * 
 * @param {Array} options - Menu items: { label, icon?, onClick, url?, disabled?, separator? }
 * @param {boolean} visible - Whether the menu is shown
 * @param {{ x: number, y: number }} position - Screen coordinates for placement
 * @param {Function} onClose - Callback to hide the menu
 */
function ContextMenu({ options, visible, position, onClose }) {
    const menuRef = useRef(null);
    const [adjustedPos, setAdjustedPos] = useState(position);

    // Close on outside click
    useEffect(() => {
        if (!visible) return;
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                onClose();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [visible, onClose]);

    // Close on Escape key
    useEffect(() => {
        if (!visible) return;
        const handleEsc = (e) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("keydown", handleEsc);
        return () => document.removeEventListener("keydown", handleEsc);
    }, [visible, onClose]);

    // Reposition menu to stay within viewport
    useEffect(() => {
        if (!visible || !menuRef.current) {
            setAdjustedPos(position);
            return;
        }
        const { innerWidth, innerHeight } = window;
        const rect = menuRef.current.getBoundingClientRect();
        let x = position.x;
        let y = position.y;

        if (x + rect.width > innerWidth) {
            x = Math.max(0, innerWidth - rect.width - 4);
        }
        if (y + rect.height > innerHeight) {
            y = Math.max(0, innerHeight - rect.height - 4);
        }
        setAdjustedPos({ x, y });
    }, [visible, position]);

    if (!visible) return null;

    return (
        <div
            className="ContextMenu"
            ref={menuRef}
            style={{
                top: adjustedPos.y,
                left: adjustedPos.x,
            }}
        >
            {options.map((opt, idx) => {
                if (opt.separator) {
                    return <div key={idx} className="ContextMenu__separator" />;
                }
                return (
                    <div
                        key={idx}
                        className={`ContextMenu__item ${opt.disabled ? "ContextMenu__item--disabled" : ""}`}
                        onMouseDown={(e) => {
                            e.preventDefault();
                            if (opt.disabled) return;

                            // Middle-click: open URL in new tab without switching focus
                            if (opt.url && e.button === 1) {
                                window.open(opt.url, "_blank", "noopener,noreferrer");
                                window.focus();
                                onClose();
                            } else if (e.button === 0) {
                                // Left-click: execute the onClick handler
                                opt.onClick?.();
                                onClose();
                            }
                        }}
                    >
                        {opt.icon && <span className="ContextMenu__item-icon">{opt.icon}</span>}
                        <span className="ContextMenu__item-label">{opt.label}</span>
                    </div>
                );
            })}
        </div>
    );
}

/**
 * Determines if the right-click target is an editable element
 * that should keep native browser context menu (spell check, paste, etc.)
 */
function shouldUseNativeContextMenu(target) {
    if (!target) return false;

    let element = target;
    while (element && element !== document.body) {
        // Text inputs and textareas always get native menu
        if (element.tagName === "INPUT" || element.tagName === "TEXTAREA") {
            return true;
        }
        // Contenteditable regions
        if (
            element.contentEditable === "true" ||
            element.getAttribute("contenteditable") === "true"
        ) {
            return true;
        }
        // Select elements
        if (element.tagName === "SELECT") {
            return true;
        }
        element = element.parentElement;
    }
    return false;
}

/**
 * Hook to use the enhanced context menu in any component.
 * 
 * Supports two usage patterns:
 * 
 * 1) Static options (global context menu):
 *    const { handleContextMenu, ContextMenuComponent } = useContextMenu([
 *      { label: "Home", icon: <i className="fas fa-home" />, onClick: () => navigate("/") },
 *    ]);
 * 
 * 2) Dynamic options (item-specific context menu):
 *    const { showContextMenu, ContextMenuComponent } = useContextMenu((contextData) => [
 *      { label: `Edit ${contextData.name}`, onClick: () => editItem(contextData.id) },
 *    ]);
 *    // Then in JSX: onContextMenu={(e) => { e.preventDefault(); showContextMenu(e.clientX, e.clientY, itemData); }}
 * 
 * @param {Array|Function} baseOptions - Static array or function(contextData) => Array
 * @returns {{ showContextMenu, hideContextMenu, handleContextMenu, ContextMenuComponent }}
 */
export function useContextMenu(baseOptions = []) {
    const [visible, setVisible] = useState(false);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [contextData, setContextData] = useState(null);
    const { enhancedRightClickEnabled } = useContextMenuSettings();

    const showContextMenu = useCallback((x, y, data = null) => {
        setPosition({ x, y });
        setContextData(data);
        setVisible(true);
    }, []);

    const hideContextMenu = useCallback(() => setVisible(false), []);

    /**
     * Attach this to a container's `onContextMenu` for automatic handling.
     * Respects native menus on editable elements and the global enable flag.
     */
    const handleContextMenu = useCallback(
        (e) => {
            if (!enhancedRightClickEnabled) return; // Feature disabled — use browser default
            if (shouldUseNativeContextMenu(e.target)) return; // Editable area — keep native

            e.preventDefault();
            showContextMenu(e.clientX, e.clientY);
        },
        [enhancedRightClickEnabled, showContextMenu]
    );

    // Always-available utility options appended to every global menu
    const alwaysOptions = [
        { separator: true },
        {
            label: "Enter Full Screen",
            icon: <i className="fas fa-expand"></i>,
            onClick: () => {
                if (document.documentElement.requestFullscreen) {
                    document.documentElement.requestFullscreen();
                } else if (document.documentElement.webkitRequestFullscreen) {
                    document.documentElement.webkitRequestFullscreen();
                }
            },
        },
        {
            label: "Refresh Page",
            icon: <i className="fas fa-sync-alt"></i>,
            onClick: () => window.location.reload(),
        },
    ];

    // Resolve options depending on whether baseOptions is static or dynamic
    const resolvedBaseOptions =
        typeof baseOptions === "function" ? baseOptions(contextData) : baseOptions;

    // For item-specific menus (contextData present + dynamic fn), show ONLY those options.
    // For global menus, append the always-available utility options.
    const allOptions =
        contextData && typeof baseOptions === "function"
            ? resolvedBaseOptions
            : [...resolvedBaseOptions, ...alwaysOptions];

    const ContextMenuComponent = (
        <ContextMenu
            options={allOptions}
            visible={visible}
            position={position}
            onClose={hideContextMenu}
        />
    );

    return {
        showContextMenu,
        hideContextMenu,
        handleContextMenu,
        ContextMenuComponent,
    };
}

export default ContextMenu;
